import { computed, inject, Injectable, signal } from "@angular/core";
import { ModelStoreAbstraction } from "@bitwarden/web-vault/app/services/store/model.store.abstraction";
import { StoreModelNames } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import {
  ModelCollectionInterface,
  SavingGoalStoreCollectionType,
  StoreModelCollection,
} from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { SavingGoalStoreCollection } from "./saving-goal.store.collection";
import {
  SavingGoalContribution,
  SavingGoalView,
} from "@bitwarden/web-vault/app/models/view/saving-goal/saving-goal.view";
import { SavingGoalUtils } from "@bitwarden/web-vault/app/models/view/saving-goal/saving-goal.utils";
import { BudgetPageInterval } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";

/**
 * Store for Saving Goals (gloss-client #4723) — persisted the same way BudgetStoreService
 * persists budgets: `savingGoal` is a DALI-backed ModelCollection that syncs to the vault via
 * the account's normal cipher sync, so a goal created on one device shows up on another.
 * Mirrors budget.store.service.ts / budget.store.collection.ts, stripped of Budget's
 * calculation-engine collections (BudgetTransactionGeneratedCollection etc.), which have no
 * SavingGoal equivalent.
 */
@Injectable({ providedIn: "root" })
export class SavingGoalStoreService
  extends ModelStoreAbstraction
  implements ModelCollectionInterface<SavingGoalStoreCollectionType>
{
  protected log: LogService = inject(LogService);
  private calculationTodayStoreService: CalculationTodayStoreService = inject(
    CalculationTodayStoreService,
  );

  /** Saving goal collection loaded from DALI */
  readonly savingGoal: SavingGoalStoreCollection = new SavingGoalStoreCollection();

  readonly data = computed<SavingGoalView[]>(() => this.savingGoal.savingGoalViews() ?? []);

  /** Which goal the detail pane is showing — plain UI-selection state, not persisted. Set only
   *  via selectGoal(), so there's one way to change it. */
  private readonly _goalToDetail = signal<SavingGoalView | null>(null);
  readonly goalToDetail = this._goalToDetail.asReadonly();

  initialize(): void {
    this.log.info("Initializing SavingGoalStoreService");
    this.savingGoal.triggerChanges();
  }

  clearStore(): void {
    this.log.info("[SAVING GOAL STORE]: Clearing SavingGoalStoreService");
    this._goalToDetail.set(null);
    this.savingGoal.clear();
  }

  protected getStorageCollection(modelName: StoreModelNames): StoreModelCollection {
    switch (modelName) {
      case "SavingGoalStoreModel":
        return this.savingGoal;
      default: {
        this.log.warning(`Need to implement SavingGoalStoreService ${modelName}`);
      }
    }
  }

  selectGoal(goal: SavingGoalView | null): void {
    this._goalToDetail.set(goal);
  }

  /**
   * Real per-period contribution history for a goal, bucketed at whichever granularity the
   * budget page's period switcher is on — recomputed on every call (not cached on the goal)
   * so switching weekly/monthly, or editing the goal's tracking method, is never stale.
   *
   * Empty while today's balances are being recalculated (see below).
   */
  contributionHistory(
    goal: SavingGoalView,
    granularity: BudgetPageInterval,
  ): SavingGoalContribution[] {
    const today = this.calculationTodayStoreService;
    // Transactions, asOf and balance must all come from the same calculation (#5383). The live
    // calculateTransaction.collection() / period.selected() move on as soon as a recalculation
    // starts, but balances() only follows once the worker resolves - walking the new deltas back
    // from the old balance gives rows that reconcile with nothing. completedCalculation() is the
    // input that balances() was computed from; it's null mid-recalculation, so report no history
    // rather than a mixed one.
    const calculation = today.completedCalculation();
    if (!calculation) {
      return [];
    }
    const asOf = calculation.period?.endDate ?? new Date();
    // Same collection the balance was computed from (aligned + revalued + filtered), so the
    // walked-back deltas actually reconcile with it. enrichTransaction is pre-alignment and
    // would not. postDate <= asOf because the balance is as-of, not all-time.
    const transactions = (calculation.transactions ?? []).filter(
      (t) => t.accountLink.id === goal.accountId && t.postDate <= asOf,
    );
    // TODO: this reads today's active Views filter/date-picker state (balanceByAccount is
    // computed from the filtered, as-of-selected-date collection) rather than an unfiltered
    // source, so a goal's headline progress can shift if the user has Views filtered to a
    // different set of accounts or scrubbed the date picker to a past date. The unfiltered
    // alternative is balanceByAccountSymbols (transaction-store level), but it's keyed by
    // symbol rather than account and would need currency conversion. Revisit if this surfaces
    // as a real bug report rather than a theoretical one.
    return SavingGoalUtils.buildContributionHistory(
      transactions,
      today.balanceByAccount.balances()?.get(goal.accountId) ?? 0,
      (rawBalance) => SavingGoalUtils.toProgress(goal, rawBalance),
      // startDate is normally always set, but the dev-only start-date field can be cleared back
      // to null (see add-saving-goal-form.component.ts's clearStartDate()) — periodsSince()
      // already treats a falsy startDate as "1 period", so just avoid the null deref here.
      // asOf (not the real "now") so the row count and the bucket anchor agree - otherwise a
      // future-dated transaction that widens the today store's endDate desyncs them (#5380).
      SavingGoalUtils.periodsSince(goal.startDate?.toISOString(), granularity, asOf),
      granularity,
      asOf,
    );
  }
}
